import {
  authenticatedApi,
  ApiError,
  API_BASE_URL
} from './apiUtils';

const appendList = (searchParams, key, value) => {
  if (Array.isArray(value) && value.length > 0) {
    searchParams.set(key, value.join(','));
  }
};

// ADR-174 §7 — the contract filters, shared by the records and the lane.
const appendContractFilters = (searchParams, params) => {
  const number = typeof params.contractNumber === 'string' ? params.contractNumber.trim() : '';
  if (number) searchParams.set('contractNumber', number);
  appendList(searchParams, 'employmentEpisodeIds', params.employmentEpisodeIds);
  if (params.concurrentOnly) searchParams.set('concurrentOnly', 'true');
};

export const employmentChangeLogApi = {
  /**
   * Employment Change Log report (ADR-145).
   *
   * One report over the whole change-record population; the audience
   * differences are filter presets, not different endpoints. The response
   * carries `summary` (counted over population + time ONLY — see
   * summary.countedOver), `items` (one row per change record, values already
   * resolved to names server-side), `pageInfo` and `meta`.
   *
   * @param {Object} params
   * @param {string}   [params.clusterId] @param {boolean} [params.includeSubUnits]
   * @param {string}   [params.businessUnitId] @param {string} [params.buScope]
   * @param {string}   [params.employeeId]
   * @param {string}   [params.representativeId] @param {string} [params.reportingScope]
   * @param {string}   [params.organizationalStatus]
   * @param {string[]} [params.fields] @param {string[]} [params.fieldGroups]
   * @param {string}   [params.changedFrom] @param {string} [params.changedTo]
   * @param {string[]} [params.detectors] @param {string[]} [params.actorTypes]
   * @param {string}   [params.actorId] @param {string} [params.ownership]
   * @param {string}   [params.explanation] - 'explained' | 'unexplained'
   * @param {string}   [params.contractNumber] @param {string[]} [params.employmentEpisodeIds]
   * @param {boolean}  [params.concurrentOnly] - ADR-174 §7
   * @param {number}   [params.windowDays] @param {boolean} [params.effectiveOnly]
   * @param {string}   [params.orderDirection] @param {number} [params.limit] @param {string} [params.cursor]
   */
  getEmploymentChangeLogReport: async (params = {}) => {
    try {
      const searchParams = new URLSearchParams();

      if (params.clusterId) searchParams.set('clusterId', params.clusterId);
      if (params.includeSubUnits !== undefined) searchParams.set('includeSubUnits', String(params.includeSubUnits));
      if (params.businessUnitId) searchParams.set('businessUnitId', params.businessUnitId);
      if (params.buScope) searchParams.set('buScope', params.buScope);
      if (params.employeeId) searchParams.set('employeeId', params.employeeId);
      if (params.representativeId) {
        searchParams.set('representativeId', params.representativeId);
        if (params.reportingScope) searchParams.set('reportingScope', params.reportingScope);
      }
      if (params.organizationalStatus) searchParams.set('organizationalStatus', params.organizationalStatus);

      appendList(searchParams, 'fields', params.fields);
      appendList(searchParams, 'fieldGroups', params.fieldGroups);
      if (params.changedFrom) searchParams.set('changedFrom', params.changedFrom);
      if (params.changedTo) searchParams.set('changedTo', params.changedTo);

      appendList(searchParams, 'detectors', params.detectors);
      appendList(searchParams, 'actorTypes', params.actorTypes);
      if (params.actorId) searchParams.set('actorId', params.actorId);
      if (params.ownership) searchParams.set('ownership', params.ownership);
      if (params.explanation) searchParams.set('explanation', params.explanation);
      appendContractFilters(searchParams, params);

      if (params.windowDays) searchParams.set('windowDays', String(params.windowDays));
      if (params.detectedFrom) searchParams.set('detectedFrom', params.detectedFrom);
      if (params.detectedTo) searchParams.set('detectedTo', params.detectedTo);
      if (params.effectiveOnly) searchParams.set('effectiveOnly', 'true');

      if (params.orderDirection) searchParams.set('orderDirection', params.orderDirection);
      if (params.limit) searchParams.set('limit', String(params.limit));
      if (params.cursor) searchParams.set('cursor', params.cursor);

      const queryString = searchParams.toString();
      return await authenticatedApi.get(
        `${API_BASE_URL}/reports/employment-changes${queryString ? `?${queryString}` : ''}`
      );
    } catch (error) {
      throw new ApiError('Failed to fetch the employment change log report', error.status || 500);
    }
  },

  /**
   * ADR-112 — the amendments lane: scheduled (Pending, window-independent
   * STATE) and optionally cancelled (window-bound EVENTS) contract
   * amendments for the same population the report covers. Sourced from the
   * event stream, never from change records; counts are never added to the
   * report's "N changes".
   *
   * @param {Object} params - population + fields/fieldGroups + window +
   *        {boolean} includeCancelled
   * @returns {Promise<{scheduled: Array, cancelled: Array, meta: Object}>}
   */
  getEmploymentChangeAmendments: async (params = {}) => {
    try {
      const searchParams = new URLSearchParams();

      if (params.clusterId) searchParams.set('clusterId', params.clusterId);
      if (params.includeSubUnits !== undefined) searchParams.set('includeSubUnits', String(params.includeSubUnits));
      if (params.businessUnitId) searchParams.set('businessUnitId', params.businessUnitId);
      if (params.buScope) searchParams.set('buScope', params.buScope);
      if (params.employeeId) searchParams.set('employeeId', params.employeeId);
      if (params.representativeId) {
        searchParams.set('representativeId', params.representativeId);
        if (params.reportingScope) searchParams.set('reportingScope', params.reportingScope);
      }
      if (params.organizationalStatus) searchParams.set('organizationalStatus', params.organizationalStatus);

      appendList(searchParams, 'fields', params.fields);
      appendList(searchParams, 'fieldGroups', params.fieldGroups);
      appendContractFilters(searchParams, params);

      if (params.windowDays) searchParams.set('windowDays', String(params.windowDays));
      if (params.detectedFrom) searchParams.set('detectedFrom', params.detectedFrom);
      if (params.detectedTo) searchParams.set('detectedTo', params.detectedTo);
      if (params.includeCancelled) searchParams.set('includeCancelled', 'true');

      const queryString = searchParams.toString();
      return await authenticatedApi.get(
        `${API_BASE_URL}/reports/employment-changes/amendments${queryString ? `?${queryString}` : ''}`
      );
    } catch (error) {
      throw new ApiError('Failed to fetch the amendments lane', error.status || 500);
    }
  },

  /**
   * The field taxonomy (label + group), served from the report service's single
   * definition so the client never restates what a contract term is.
   * @returns {Promise<{groups: Object, fields: Array, retentionDays: number, defaultWindowDays: number}>}
   */
  getFieldCatalog: async () => {
    try {
      return await authenticatedApi.get(`${API_BASE_URL}/reports/employment-changes/field-catalog`);
    } catch (error) {
      throw new ApiError('Failed to fetch the change-log field catalog', error.status || 500);
    }
  },
};
